/**
 * Security Event Logging
 * Tracks security-related events for monitoring and forensics
 * 
 * Logs events to Firestore for:
 * - Breach detection
 * - Anomaly detection
 * - Forensic analysis
 * - Compliance (GDPR breach notification)
 */

import { db, auth } from '../api/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import logger from './logger';

/**
 * Log a security event
 * @param {string} eventType - Type of event
 * @param {Object} details - Event details
 */
export const logSecurityEvent = async (eventType, details = {}) => {
  try {
    const currentUser = auth.currentUser;
    
    // Get client info
    const clientInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      online: navigator.onLine,
      timestamp: new Date().toISOString(),
    };

    // Compile security log entry
    const logEntry = {
      eventType,
      userId: currentUser?.uid || 'anonymous',
      email: currentUser?.email || null,
      timestamp: serverTimestamp(),
      clientTimestamp: Date.now(),
      details: {
        ...details,
        ...clientInfo
      },
      severity: calculateSeverity(eventType),
      requiresInvestigation: requiresInvestigation(eventType),
    };

    // Log to Firestore (server-side collection)
    // Note: This will fail client-side due to security rules
    // In production, this should be called from server-side only
    // For now, we'll also log to console
    logger.info('SecurityLogger', 'Info', eventType, logEntry);
    
    // Optional: Send to server endpoint for logging
    try {
      await fetch('/api/log-security-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logEntry)
      });
    } catch (serverError) {
      logger.warn('SecurityLogger', 'Warning', serverError.message);
      // Don't throw - logging failure shouldn't break user flow
    }

  } catch (error) {
    logger.error('SecurityLogger', 'Error', error);
    // Don't throw - logging failure shouldn't break user flow
  }
};

/**
 * Calculate severity based on event type
 */
const calculateSeverity = (eventType) => {
  const highSeverity = [
    'auth_failed_multiple',
    'account_deleted',
    'data_exported',
    'suspicious_login_location',
    'brute_force_detected',
    'unauthorized_access_attempt',
  ];

  const mediumSeverity = [
    'login_failed',
    'password_reset',
    'email_changed',
    'subscription_upgraded',
    'unusual_activity',
  ];

  if (highSeverity.includes(eventType)) return 'HIGH';
  if (mediumSeverity.includes(eventType)) return 'MEDIUM';
  return 'LOW';
};

/**
 * Determine if event requires investigation
 */
const requiresInvestigation = (eventType) => {
  const investigationRequired = [
    'auth_failed_multiple',
    'suspicious_login_location',
    'brute_force_detected',
    'unauthorized_access_attempt',
    'data_exported', // Track who exports data
  ];

  return investigationRequired.includes(eventType);
};

/**
 * Predefined security event types
 */
export const SECURITY_EVENTS = {
  // Authentication Events
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  LOGIN_FAILED_MULTIPLE: 'auth_failed_multiple',
  LOGOUT: 'logout',
  
  // Account Events
  ACCOUNT_CREATED: 'account_created',
  ACCOUNT_DELETED: 'account_deleted',
  EMAIL_CHANGED: 'email_changed',
  PASSWORD_RESET: 'password_reset',
  
  // Data Events
  DATA_EXPORTED: 'data_exported',
  DATA_DELETION_REQUESTED: 'data_deletion_requested',
  
  // Suspicious Activity
  SUSPICIOUS_LOGIN_LOCATION: 'suspicious_login_location',
  BRUTE_FORCE_DETECTED: 'brute_force_detected',
  UNUSUAL_ACTIVITY: 'unusual_activity',
  UNAUTHORIZED_ACCESS: 'unauthorized_access_attempt',
  
  // Subscription Events
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',
  PAYMENT_FAILED: 'payment_failed',
};

/**
 * Helper functions for common events
 */
export const logLoginAttempt = (success, method) => {
  logSecurityEvent(
    success ? SECURITY_EVENTS.LOGIN_SUCCESS : SECURITY_EVENTS.LOGIN_FAILED,
    { method, success }
  );
};

export const logAccountCreation = (method) => {
  logSecurityEvent(SECURITY_EVENTS.ACCOUNT_CREATED, { method });
};

export const logAccountDeletion = (reason) => {
  logSecurityEvent(SECURITY_EVENTS.ACCOUNT_DELETED, { reason });
};

export const logDataExport = () => {
  logSecurityEvent(SECURITY_EVENTS.DATA_EXPORTED, { 
    requestedBy: 'user',
    reason: 'gdpr_request'
  });
};

export const logPasswordReset = () => {
  logSecurityEvent(SECURITY_EVENTS.PASSWORD_RESET, {});
};

export const logSuspiciousActivity = (activityType, details) => {
  logSecurityEvent(SECURITY_EVENTS.UNUSUAL_ACTIVITY, {
    activityType,
    ...details
  });
};

